<script setup lang="ts">
import { useNuxtApp } from '@nuxt/bridge/dist/runtime'
const PageTitle = () => import('@/components/molecules/PageTitle.vue')
const GachaSwiper = () => import('@/components/organisms/gacha/GachaSwiper.vue')
const GachaHistoryModal = () => import('@/components/organisms/gacha/GachaHistoryModal.vue')
const GachaResultModal = () => import('@/components/organisms/gacha/GachaResultModal.vue')
const GachaDropRateModal = () => import('@/components/organisms/gacha/GachaDropRateModal.vue')
const GachaPaymentTypeModal = () => import('@/components/organisms/gacha/GachaPaymentTypeModal.vue')
const GachaMovieModal = () => import('@/components/organisms/gacha/GachaMovieModal.vue')
const MyBalanceBox = () => import('@/components/molecules/MyBalanceBox.vue')
const nl2br = () => import('vue-nl2br')

const { $toast, $dayjs } = useNuxtApp()
import { useGachaStore } from '@/stores/useGachaStore'
import { useRewardStore } from '@/stores/useRewardStore'
import { useConsumableStore } from '@/stores/useConsumableStore'
import { onBeforeMount, ref, watch } from 'vue'

const isLoading = ref(true)
const isGachaHistoryModalShown = ref(false)
const isGachaResultModalShown = ref(false)
const isGachaDropRateModalShown = ref(false)
const isGachaPaymentTypeModalShown = ref(false)
const isGachaMovieModalShown = ref(false)
const results = ref([])
const rewards = ref([])
const selectedGachaType = ref(null)
const selectedPaymentType = ref(null)
const isMulti = ref(false)
const isPullGacha = ref(false)
const gachaAnimationType = ref(null)
const firstGachaPaymentType = ref(null)

const getMode = useRewardStore().getMode

onBeforeMount(async () => {
  await useGachaStore().fetchGachaTypes()
  await useConsumableStore().fetchConsumeMyItems()
  isLoading.value = false
})

async function pullGacha(paymentType) {
  try {
    selectedPaymentType.value = paymentType
    await useGachaStore().gacha(
      selectedGachaType.value.gachaType,
      selectedPaymentType.value.gachaPaymentType,
      isMulti.value
    )
    results.value = useGachaStore().getGachaResult
    gachaAnimationType.value = useGachaStore().getGachaAnimationType
    isGachaPaymentTypeModalShown.value = false
    isGachaMovieModalShown.value = true
  } catch (e) {
    console.error(e)
    $toast.error('Error.')
  }
  await useConsumableStore().fetchConsumeMyItems()
}

function continuePull() {
  isGachaResultModalShown.value = false
  isGachaPaymentTypeModalShown.value = true
}

async function openGachaHistoryModal(gachaType) {
  await useGachaStore().fetchUserGachaHistories(gachaType)
  isGachaHistoryModalShown.value = true
}

function openGachaResultModal(r) {
  results.value = r
  isGachaResultModalShown.value = true
}

function openGachaDropRateModal(r) {
  rewards.value = r
  isGachaDropRateModalShown.value = true
}

function openSinglePaymentTypeModal(r) {
  selectedGachaType.value = r
  isMulti.value = false
  isGachaPaymentTypeModalShown.value = true
}

function openMultiPaymentTypeModal(r) {
  selectedGachaType.value = r
  isMulti.value = true
  isGachaPaymentTypeModalShown.value = true
}

function closeGachaMovieModal() {
  isGachaMovieModalShown.value = false
  isPullGacha.value = true
  isGachaResultModalShown.value = true
}

function getFirstGachaPaymentType(type) {
  firstGachaPaymentType.value = type.paymentItemsList
}

</script>

<template>
  <div>
    <PageTitle backPrev>
      {{ $t('pages.gacha') }}
      <template slot="helpTitle">
        <h3>{{ $t('gacha.help.title') }}</h3>
      </template>
      <template slot="help">
        <nl2br tag="p" :text="$t('gacha.help.detail')" />
      </template>
    </PageTitle>

    <MyBalanceBox
      v-if="firstGachaPaymentType"
      class="fixed right-[2%] md:-translate-y-4 w-[7rem] [&_span]:p-0 md:w-[10rem] z-50"
      :mode="getMode(firstGachaPaymentType[0])"
      :key="firstGachaPaymentType.itemId"
    />

    <GachaSwiper
      v-if="!isLoading"
      @onHistory="openGachaHistoryModal"
      @onRate="openGachaDropRateModal"
      @onSingle="openSinglePaymentTypeModal"
      @onMulti="openMultiPaymentTypeModal"
      @onFirstPaymentType="getFirstGachaPaymentType"
    />

    <GachaHistoryModal
      v-if="isGachaHistoryModalShown"
      @onResult="openGachaResultModal"
      @modal-close="isGachaHistoryModalShown = false"
    />

    <GachaResultModal
      v-if="isGachaResultModalShown"
      :results="results"
      :isPullGacha="isPullGacha"
      :animationType="gachaAnimationType"
      @modal-close="
        isGachaResultModalShown = false
        isPullGacha = false
        selectedPaymentType = null
      "
      @continue-pull="continuePull"
    />

    <GachaDropRateModal
      v-if="isGachaDropRateModalShown"
      :rewards="rewards"
      @modal-close="isGachaDropRateModalShown = false"
    />

    <GachaPaymentTypeModal
      v-if="isGachaPaymentTypeModalShown"
      :gachaType="selectedGachaType"
      :isMulti="isMulti"
      :currentPaymentType="selectedPaymentType"
      @onPayment="pullGacha"
      @modal-close="
        isGachaPaymentTypeModalShown = false
        isPullGacha = false
        selectedPaymentType = null
      "
    />

    <GachaMovieModal
      v-if="isGachaMovieModalShown"
      @modal-close="closeGachaMovieModal"
      :animationType="gachaAnimationType"
      :key="$dayjs().unix()"
    />
  </div>
</template>
